<template>
    <div class="tier-progress-container" :class="stateClass">

        <!-- Locked icon if state locked -->
        <div class="locked-container">
            <span class="locked-icon-mask" aria-label="locked-icon"></span>
            <span>Tier Locked</span>
        </div>
        <div class="tier-to-container">
            <!-- Current Tier -->
            <div class="tier-container tier-current">
                <img v-if="currentTier.icon && !currentTier.icon.includes('fas ')" :src="currentTier.icon" :alt="currentTier.name">
                <i v-else :class="currentTier.icon || 'fas fa-medal'" class="fa-3x tier-icon-color"></i>
                <div class="tier-title">
                    <h2>{{ currentTier.name.length > 15 ? currentTier.name.substring(0, 25) : currentTier.name }}</h2>
                    <span>{{ currentTier.amount }}</span>
                </div>
            </div>

            <!-- Next Tier -->
            <div class="tier-container tier-next">
                <img v-if="nextTier.icon && !nextTier.icon.includes('fas ')" :src="nextTier.icon" :alt="nextTier.name">
                <i v-else :class="nextTier.icon || 'fas fa-medal'" class="fa-3x tier-icon-color"></i>
                <div class="tier-title">
                    <h2>{{ nextTier.name.length > 15 ? nextTier.name.substring(0, 25) : nextTier.name }}</h2>
                    <span>{{ nextTier.amount }}</span>
                </div>
            </div>
        </div>

        <!-- Progress Bar -->
        <div class="progress-container">
            <div class="progress-bar">
                <div class="progress-fill" :style="{ width: progressPercentage + '%' }">
                    <span>Max Tier</span>
                </div>
            </div>

            <!-- Label -->
            <div class="progress-label" :style="{ left: clampedLabelPosition + '%' }">
                {{ currentAmount }}
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MemberTierProgress',
    props: {
        // State: 'passed', 'active', 'locked', 'max'
        state: {
            type: String,
            default: 'active',
            validator: (value) => ['passed', 'active', 'locked', 'max'].includes(value)
        },
        // Is this card selected (centered)?
        isSelected: {
            type: Boolean,
            default: false
        },
        // Current tier info
        currentTier: {
            type: Object,
            default: () => ({
                name: 'GOLD',
                amount: 'IDR. 1.200.000',
                icon: '/img/member-img/tier-gold-example.svg'
            })
        },
        // Next tier info
        nextTier: {
            type: Object,
            default: () => ({
                name: 'PLATINUM',
                amount: 'IDR. 3.200.000',
                icon: '/img/member-img/tier-platinum-example.svg'
            })
        },
        // Current amount/progress
        currentAmount: {
            type: String,
            default: '2.000.000'
        },
        // Progress percentage (0-100)
        progressPercentage: {
            type: Number,
            default: 60
        }
    },
    computed: {
        stateClass() {
            const classes = [`state-${this.state}`];
            if (this.isSelected) {
                classes.push('is-selected');
            }
            return classes.join(' ');
        },
        clampedLabelPosition() {
            // Clamp label position between 16% and 84% to prevent overflow
            const percentage = this.progressPercentage;
            if (percentage < 16) return 16;
            if (percentage > 84) return 84;
            return percentage;
        }
    }
}
</script>

<style scoped>
    /* ===== BASE STYLES ===== */
    .tier-progress-container {
        display: flex;
        flex-direction: column;
        gap: 16px;
        background-color: white;
        border: 1px solid #E1E4EA;
        border-radius: 12px;
        padding: 24px;
        padding-bottom: 60px;
        width: 100%;
        min-width: 264px;
        overflow: hidden;
    }

    .tier-to-container {
        display: flex;
        justify-content: space-between;
    }

    .tier-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 12px;
        width: fit-content;
    }

    .tier-container img {
        width: 64px;
    }

    .tier-title {
        display: flex;
        justify-content: center;
        flex-direction: column;
        gap: 6px;
    }

    .tier-container h2 {
        font-size: 16px;
        font-weight: 600;
        margin: 0 !important;
        text-align: center;
    }

    .tier-container span {
        font-size: 12px;
        color: #717784;
        letter-spacing: -2%;
        text-align: center;
    }

    /* ===== PROGRESS BAR ===== */
    .progress-container {
        position: relative;
    }

    .progress-bar {
        width: 100%;
        height: 14px;
        background-color: #D6DDDD;
        border-radius: 12px;
        overflow: visible;
    }

    .progress-fill {
        height: 18px;
        background-color: var(--primary-color, #F93E3E);
        border-radius: 16px;
        position: relative;
        transition: width 0.3s ease;
        min-width: 20px;
    }

    .progress-fill::after {
        content: '';
        position: absolute;
        right: 0px;
        top: 50%;
        transform: translateY(-50%);
        width: 20px;
        height: 20px;
        background-color: var(--primary-color, #F93E3E);
        border-radius: 50%;
    }

    .progress-fill span {
        display: none;
    }

    .progress-label {
        font-size: 12px;
        font-weight: 500;
        display: block;
        padding: 4px 12px;
        border-radius: 1rem;
        color: white;
        background-color: var(--primary-color, #F93E3E);
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        top: 24px;
        max-width: calc(100% - 20px);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        text-align: center;
    }

    .locked-container {
        display: none;
    }



    /* ===== STATE: PASSED ===== */
    .state-passed .progress-fill::after {
        display: none;
    }

    .state-passed .progress-label {
        display: none;
    }




    /* ===== STATE: ACTIVE ===== */
    .state-active .progress-label {
        display: none;
    }




    /* ===== STATE: LOCKED ===== */
    .state-locked {
        position: relative;
    }

    .state-locked .tier-container {
        opacity: 0.5 !important;
    }

    .state-locked .progress-fill {
        display: none;
    }

    .state-locked .progress-label {
        display: none;
    }

    .state-locked .progress-container {
        opacity: 0.5 !important;
    }

    .state-locked .locked-container {
        display: flex;
        z-index: 50;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 8px;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    .state-locked .locked-container span {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 4px 12px;
        background-color: var(--primary-color, #F93E3E);
        border-radius: 16px;
        color: white;
        font-size: 12px;
        font-weight: 550;
    }

    .locked-icon-mask {
        display: inline-block;
        width: 32px;
        height: 32px;
        background-color: var(--primary-color, #F93E3E);
        -webkit-mask-image: url('/img/member-icon/locked-icon.svg');
        -webkit-mask-size: contain;
        -webkit-mask-repeat: no-repeat;
        -webkit-mask-position: center;
        mask-image: url('/img/member-icon/locked-icon.svg');
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
    }


    /* ===== STATE: MAX ===== */
    .state-max .progress-fill::after {
        display: none;
    }

    .state-max .progress-label {
        display: none;
    }

    .state-max .progress-fill span {
        display: block;
        font-size: 12px;
        font-weight: 550;
    }



    /* ===== SELECTED STATE ===== */
    .is-selected {
        min-height: 178px;
        border: solid 1px var(--primary-border, #F93E3E);
    }

    .tier-icon-color {
        color: var(--primary-color, #F93E3E);
    }

    .is-selected.state-active .progress-label {
        display: flex;
    }



    /* ===== RESPONSIVE: MOBILE ===== */
    @media (max-width: 900px) {
        .tier-progress-container {
            max-width: 264px;
            min-width: 264px;
            padding: 18px;
            gap: 12px;
        }

        .tier-container img {
            width: 48px;
        }

        .tier-title {
            gap: 4px;
        }

        .tier-container h2 {
            font-size: 12px;
            font-weight: 550;
        }

        .tier-container span {
            font-size: 0.5rem;
        }

        .progress-bar {
            height: 12px;
        }

        .progress-fill {
            height: 1rem;
            min-width: 16px;
        }

        .progress-fill::after {
            width: 16px;
            height: 16px;
        }

        .progress-label {
            font-size: 10px;
            padding: 2px 10px;
            top: 20px;
        }

        .state-locked .locked-container span {
            padding: 2px 10px;
            font-size: 10px;
        }

        .state-max .progress-fill span {
            font-size: 10px;
        }
    }
</style>